import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  FileImage,
  LockKeyhole,
  Pencil,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { storage, db, auth } from "./firebase";
import { saveEvolutionSignature, signatureErrorMessage } from "./evolution/signature";
import { requestVexaConfirm } from "./notifications/dialogService";
import {
  arrayUnion,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "./access/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";

const statusText = (value) => {
  const key = String(value || "").toLowerCase();
  if (key === "pagada") return "Pagada";
  if (key === "cancelada") return "Cancelada";
  if (key === "activa") return "Activa";
  return value || "Registro clínico";
};

export default function EvolutionCard({ cita, clinicId, formatearFecha }) {
  const signatureRef = useRef(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signatureEmpty, setSignatureEmpty] = useState(true);
  const [savingSignature, setSavingSignature] = useState(false);
  const [signatureMessage, setSignatureMessage] = useState("");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    motivo: cita?.motivo || "",
    tratamiento: cita?.tratamiento || "",
    notas: cita?.notas || "",
    proximo: cita?.proximo || "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(-1);
  const [actionError, setActionError] = useState("");
  const signed = Boolean(cita?.firmaURL || cita?.firmadaAt || cita?.firmadaPor);
  const photos = Array.isArray(cita?.imagenes) ? cita.imagenes : [];
  const citaRef = doc(db, "clinics", clinicId, "citas", cita.id);
  const date = formatearFecha ? formatearFecha(cita?.fecha) : cita?.fecha || "Sin fecha";

  useEffect(() => {
    setEditForm({
      motivo: cita?.motivo || "",
      tratamiento: cita?.tratamiento || "",
      notas: cita?.notas || "",
      proximo: cita?.proximo || "",
    });
  }, [cita?.id, cita?.motivo, cita?.notas, cita?.proximo, cita?.tratamiento]);

  const closeSignature = () => {
    if (savingSignature) return;
    setSignatureOpen(false);
    setSignatureEmpty(true);
    setSignatureMessage("");
  };

  const saveSignature = async () => {
    if (signatureEmpty || savingSignature) return;
    setSavingSignature(true);
    setSignatureMessage("");
    try {
      const dataUrl = signatureRef.current?.getCanvas()?.toDataURL("image/png");
      await saveEvolutionSignature(clinicId, cita, dataUrl);
      closeSignature();
    } catch (error) {
      setSignatureMessage(signatureErrorMessage(error));
    } finally {
      setSavingSignature(false);
    }
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (signed || busy) return;
    if (!editForm.motivo.trim() && !editForm.tratamiento.trim() && !editForm.notas.trim()) {
      setActionError("Agrega al menos un dato clínico antes de guardar.");
      return;
    }
    setBusy(true);
    setActionError("");
    try {
      await updateDoc(citaRef, {
        motivo: editForm.motivo.trim(),
        tratamiento: editForm.tratamiento.trim(),
        notas: editForm.notas.trim(),
        proximo: editForm.proximo.trim(),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || "",
      });
      setEditing(false);
    } catch (error) {
      setActionError(error?.message || "No pudimos guardar los cambios.");
    } finally {
      setBusy(false);
    }
  };

  const removeNote = async () => {
    if (signed || busy) return;
    const confirmed = await requestVexaConfirm("¿Eliminar esta nota de evolución definitivamente?");
    if (!confirmed) return;
    setBusy(true);
    setActionError("");
    try {
      await deleteDoc(citaRef);
    } catch (error) {
      setActionError(error?.code === "permission-denied" ? "No tienes permiso para eliminar esta nota." : "No pudimos eliminar la nota. Intenta nuevamente.");
      setBusy(false);
    }
  };

  const uploadPhotos = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || signed) return;
    setUploading(true);
    setActionError("");
    try {
      for (const file of files) {
        const path = `clinics/${clinicId}/evolution/${cita.id}/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        await updateDoc(citaRef, {
          imagenes: arrayUnion({ url, path, nombre: file.name, createdAt: new Date() }),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || "",
        });
      }
    } catch (error) {
      setActionError(error?.message || "No pudimos subir las imágenes.");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (index) => {
    if (signed || busy) return;
    const confirmed = await requestVexaConfirm("¿Eliminar esta fotografía clínica?");
    if (!confirmed) return;
    const next = [...photos];
    const photo = next[index];
    next.splice(index, 1);
    setBusy(true);
    try {
      await updateDoc(citaRef, { imagenes: next, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.uid || "" });
      if (photo?.path) await deleteObject(ref(storage, photo.path));
      setPhotoIndex(-1);
    } catch (error) {
      setActionError(error?.message || "No pudimos eliminar la fotografía.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="vd-evolution-record">
      <span className="vd-evolution-record__dot" aria-hidden="true" />
      <div className="vd-evolution-record__date"><strong>{date}</strong><span>{cita?.hora || "Hora no indicada"}</span></div>
      <div className="vd-evolution-record__body">
        <header className="vd-evolution-record__header">
          <div><span className="vd-evolution-record__type">{cita?.tipoRegistro === "evolucion" || cita?.origen === "manual" ? "Nota de evolución" : "Consulta"}</span><h3>{cita?.motivo || cita?.tratamiento || "Consulta sin motivo"}</h3><p>{cita?.doctorNombre || "Profesional no indicado"}</p></div>
          <div className="vd-evolution-record__status"><span className={`vd-evolution-status vd-evolution-status--${signed ? "signed" : String(cita?.estado || "active").toLowerCase()}`}><span />{signed ? "Firmada" : statusText(cita?.estado)}</span></div>
        </header>
        {actionError && <div className="vd-evolution-record__error" role="alert">{actionError}</div>}
        <div className="vd-evolution-record__summary">
          <div><small>Tratamiento</small><strong>{cita?.tratamiento || "Sin registrar"}</strong></div>
          <div><small>Evolución</small><p>{cita?.notas || "Sin nota clínica"}</p></div>
          <div><small>Próximo paso</small><strong>{cita?.proximo || "Sin indicar"}</strong></div>
        </div>
        <div className="vd-evolution-record__footer">
          <div className="vd-evolution-record__financial"><span>Consulta {Number(cita?.presupuesto || 0) ? `· $${Number(cita.presupuesto).toLocaleString("es-MX")}` : ""}</span>{Number(cita?.saldo || 0) > 0 && <b>Saldo {`$${Number(cita.saldo).toLocaleString("es-MX")}`}</b>}</div>
          <div className="vd-evolution-record__actions">{signed ? <span className="vd-evolution-signed-note"><LockKeyhole size={15} /> Registro protegido</span> : <><button type="button" className="vd-evolution-card-button" onClick={() => setEditing(true)}><Pencil size={15} /> Editar</button><button type="button" className="vd-evolution-card-button vd-evolution-card-button--danger" onClick={removeNote} disabled={busy}><Trash2 size={15} /> Eliminar</button></>}<button type="button" className="vd-evolution-card-button vd-evolution-card-button--primary" onClick={() => setSignatureOpen(true)}>{signed ? <Check size={15} /> : <LockKeyhole size={15} />} {signed ? "Ver firma" : "Firmar nota"}</button></div>
        </div>
        <div className="vd-evolution-attachments"><div className="vd-evolution-attachments__heading"><span><FileImage size={16} /> Fotografías clínicas <b>{photos.length}</b></span>{!signed && <label className="vd-evolution-upload"><Upload size={15} /> {uploading ? "Subiendo..." : "Agregar"}<input type="file" accept="image/*" multiple disabled={uploading} onChange={uploadPhotos} /></label>}</div>{photos.length ? <div className="vd-evolution-photo-grid">{photos.map((photo, index) => <button type="button" key={`${photo.path || photo.url}-${index}`} onClick={() => setPhotoIndex(index)}><img src={photo.url} alt={photo.nombre || `Fotografía ${index + 1}`} /></button>)}</div> : <p className="vd-evolution-attachments__empty">Agrega fotos para documentar el avance clínico.</p>}</div>
      </div>

      {editing && !signed && <div className="vd-evolution-modal-layer" role="presentation" onMouseDown={() => setEditing(false)}><form className="vd-evolution-modal" onSubmit={saveEdit} onMouseDown={(event) => event.stopPropagation()}><header><div><span className="vd-evolution-eyebrow"><Pencil size={15} /> Edición clínica</span><h2>Editar evolución</h2><p>Los cambios se sincronizan con el expediente.</p></div><button type="button" className="vd-evolution-icon-button" onClick={() => setEditing(false)} aria-label="Cerrar edición"><X size={19} /></button></header><div className="vd-evolution-modal__body">{actionError && <div className="vd-evolution-form-error" role="alert">{actionError}</div>}<label className="vd-evolution-field"><span className="vd-evolution-field__label">Motivo de consulta</span><input value={editForm.motivo} onChange={(event) => setEditForm((before) => ({ ...before, motivo: event.target.value }))} /></label><label className="vd-evolution-field"><span className="vd-evolution-field__label">Tratamiento realizado</span><input value={editForm.tratamiento} onChange={(event) => setEditForm((before) => ({ ...before, tratamiento: event.target.value }))} /></label><label className="vd-evolution-field"><span className="vd-evolution-field__label">Nota clínica</span><textarea rows={6} value={editForm.notas} onChange={(event) => setEditForm((before) => ({ ...before, notas: event.target.value }))} /></label><label className="vd-evolution-field"><span className="vd-evolution-field__label">Próximo paso</span><input value={editForm.proximo} onChange={(event) => setEditForm((before) => ({ ...before, proximo: event.target.value }))} /></label></div><footer><button type="button" className="vd-evolution-secondary" onClick={() => setEditing(false)}>Cancelar</button><button type="submit" className="vd-evolution-primary" disabled={busy}>{busy ? "Guardando..." : <><Save size={16} /> Guardar cambios</>}</button></footer></form></div>}

      {signatureOpen && <div className="vd-evolution-modal-layer" role="presentation" onMouseDown={closeSignature}><div className="vd-evolution-signature-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}><header><div><span className="vd-evolution-eyebrow"><LockKeyhole size={15} /> Firma clínica</span><h2>{signed ? "Firma de la consulta" : "Firmar nota de evolución"}</h2><p>{signed ? "Esta nota está protegida y no se puede editar." : "Traza la firma del paciente para cerrar el registro."}</p></div><button type="button" className="vd-evolution-icon-button" onClick={closeSignature} aria-label="Cerrar firma"><X size={19} /></button></header>{signed ? <div className="vd-evolution-signed-preview"><img src={cita.firmaURL} alt="Firma del paciente" /><span><Check size={16} /> Firma guardada</span></div> : <><div className="vd-evolution-signature-pad"><SignatureCanvas ref={signatureRef} penColor="#122c4a" canvasProps={{ className: "vd-evolution-signature-canvas" }} onEnd={() => setSignatureEmpty(false)} /><span>Firma aquí</span></div>{signatureMessage && <div className="vd-evolution-form-error" role="alert">{signatureMessage}</div>}<footer><button type="button" className="vd-evolution-secondary" onClick={() => { signatureRef.current?.clear(); setSignatureEmpty(true); }}>Limpiar</button><button type="button" className="vd-evolution-primary" disabled={signatureEmpty || savingSignature} onClick={saveSignature}>{savingSignature ? "Guardando..." : <><Save size={16} /> Guardar firma</>}</button></footer></>}</div></div>}

      {photoIndex >= 0 && photos[photoIndex] && <div className="vd-evolution-lightbox" role="dialog" aria-label="Vista de fotografía" onClick={() => setPhotoIndex(-1)}><button type="button" className="vd-evolution-lightbox__close" onClick={() => setPhotoIndex(-1)} aria-label="Cerrar fotografía"><X /></button>{photoIndex > 0 && <button type="button" className="vd-evolution-lightbox__nav vd-evolution-lightbox__nav--left" onClick={(event) => { event.stopPropagation(); setPhotoIndex((index) => index - 1); }} aria-label="Fotografía anterior"><ChevronLeft /></button>}<img src={photos[photoIndex].url} alt={photos[photoIndex].nombre || "Fotografía clínica"} onClick={(event) => event.stopPropagation()} />{photoIndex < photos.length - 1 && <button type="button" className="vd-evolution-lightbox__nav vd-evolution-lightbox__nav--right" onClick={(event) => { event.stopPropagation(); setPhotoIndex((index) => index + 1); }} aria-label="Fotografía siguiente"><ChevronRight /></button>}<span className="vd-evolution-lightbox__counter">{photoIndex + 1} / {photos.length}</span>{!signed && <button type="button" className="vd-evolution-lightbox__delete" onClick={(event) => { event.stopPropagation(); removePhoto(photoIndex); }}><Trash2 size={16} /> Eliminar fotografía</button>}</div>}
    </article>
  );
}
